import { format } from "date-fns";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useParams } from "react-router-dom";

import {
  ContentDevice,
  PopupContentReportPageContainer_Query,
} from "~/src/__relay_artifacts__/PopupContentReportPageContainer_Query.graphql";
import { DateRangeContext } from "~/src/components/features/global/HeaderNavigation/DateRangeField";

import { ContentElementMetric, ContentReportPage } from "../ContentReportPage";

const query = graphql`
  query PopupContentReportPageContainer_Query(
    $popupSlug: String!
    $popupGroupSlug: String!
    $input: ContentReportInput!
  ) {
    popupGroup(slug: $popupGroupSlug) {
      name
      page {
        url
      }
    }
    popup(slug: $popupSlug) {
      name
      reportContent(input: $input) {
        totalUniqueUsers
        totalViews
        totalGoals
        viewportWidth
        viewportHeight
        previewImageUrl
        elements {
          name
          previewImageUrl
          updatedDate
          views
          viewRate
          averageViewTime
          goals
          goalRate
          clicks
          clickRate
          position {
            x
            y
            width
            height
          }
        }
      }
    }
  }
`;

export const PopupContentReportPageContainer: FC = () => {
  const {
    dateRange: [startOn, endOn],
  } = useContext(DateRangeContext);
  const { siteSlug = "", popupGroupSlug = "", patternSlug = "" } = useParams();
  const [deviceType, setDeviceType] = useState<ContentDevice>("PC");

  const handleDeviceChange = useCallback(
    (dt: string) => setDeviceType(dt as ContentDevice),
    []
  );

  const { popup, popupGroup } =
    useLazyLoadQuery<PopupContentReportPageContainer_Query>(
      query,
      {
        popupSlug: patternSlug,
        popupGroupSlug,
        input: {
          startOn: format(startOn, "yyyy-MM-dd"),
          endOn: format(endOn, "yyyy-MM-dd"),
          deviceType,
        },
      },
      {
        fetchKey: `${format(startOn, "yyyy-MM-dd")}-${format(
          endOn,
          "yyyy-MM-dd"
        )}-${deviceType}`,
        fetchPolicy: "network-only",
      }
    );

  if (!popup) throw new Error("assertion failed");

  const breadcrumbs = useMemo(
    () => [
      { label: "ポップアップ一覧", path: `/sites/${siteSlug}/popup_groups` },
      {
        label: "ポップアップ詳細",
        path: `/sites/${siteSlug}/popup_groups/${popupGroupSlug}`,
      },
      {
        label: "パターン詳細",
        path: `/sites/${siteSlug}/popup_groups/${popupGroupSlug}/patterns/${patternSlug}`,
      },
      {
        label: "コンテンツレポート",
        path: `/sites/${siteSlug}/popup_groups/${popupGroupSlug}/patterns/${patternSlug}/content_report`,
      },
    ],
    [patternSlug, popupGroupSlug, siteSlug]
  );

  const elements = useMemo((): ContentElementMetric[] => {
    return (popup.reportContent?.elements ?? []).map((el) => ({
      name: el.name,
      previewImageUrl: el.previewImageUrl,
      updatedDate: el.updatedDate,
      views: el.views,
      viewRate: el.viewRate,
      averageViewTime: el.averageViewTime,
      goals: el.goals,
      goalRate: el.goalRate,
      clicks: el.clicks,
      clickRate: el.clickRate,
      position: el.position
        ? {
            x: el.position.x,
            y: el.position.y,
            width: el.position.width,
            height: el.position.height,
          }
        : null,
    }));
  }, [popup.reportContent?.elements]);

  return (
    <ContentReportPage
      title="ポップアップ コンテンツレポート"
      breadcrumbs={breadcrumbs}
      pageUrl={popupGroup?.page.url ?? ""}
      deviceType={deviceType}
      onDeviceChange={handleDeviceChange}
      totalUniqueUsers={popup.reportContent?.totalUniqueUsers ?? 0}
      totalViews={popup.reportContent?.totalViews ?? 0}
      totalGoals={popup.reportContent?.totalGoals ?? 0}
      viewportWidth={popup.reportContent?.viewportWidth}
      viewportHeight={popup.reportContent?.viewportHeight}
      previewImageUrl={popup.reportContent?.previewImageUrl}
      elements={elements}
    />
  );
};
